import { createContext, useContext, useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from "react";
import { defaultLearningDisplay, learningDisplayFrom, printHelpDurationMs, supportsLearningHandwriting, type LearningDisplay } from "@calega/content";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { exerciseRunStartedEvent } from "./learning-api";
import { ExerciseReflectionBoundary } from "./ExerciseReflectionBoundary";
import { ExercisePauseBoundary } from "./ExercisePauseBoundary";

type Item = { runId: string; itemKey: string; preparePause?: () => Promise<string> };
const ItemContext = createContext<((item: Item) => void) | null>(null);

export function useLearningDisplayItem(runId: string | null | undefined, itemKey: string | number, active = true, preparePause?: () => Promise<string>) {
  const setItem = useContext(ItemContext);
  useLayoutEffect(() => { setItem?.({ runId: runId ?? "", itemKey: active ? String(itemKey) : "", preparePause }); }, [setItem, runId, itemKey, active, preparePause]);
}

export function LearningDisplayBoundary({ exerciseType, children }: { exerciseType: string; children: ReactNode }) {
  const realtime = useRealtime();
  const [item, setItem] = useState<Item>({ runId: "", itemKey: "" });
  const [display, setDisplay] = useState<LearningDisplay>(defaultLearningDisplay);
  const [localHelp, setLocalHelp] = useState<(Item & { until: number }) | null>(null);
  const [clock, setClock] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadedFont, setLoadedFont] = useState("");
  const [failedFont, setFailedFont] = useState("");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [presented, setPresented] = useState(false);
  const [message, setMessage] = useState("");
  const handwriting = supportsLearningHandwriting(exerciseType);
  const font = handwriting ? display.learningFont : "GRUND";
  const fontReady = loadedFont === font;
  const synchronizedHelp = realtime.printHelp;
  const deadline = realtime.session
    ? synchronizedHelp?.runId === item.runId && synchronizedHelp.itemKey === item.itemKey ? synchronizedHelp.until : 0
    : localHelp?.runId === item.runId && localHelp.itemKey === item.itemKey ? localHelp.until : 0;
  const printing = Boolean(item.itemKey && deadline > realtime.serverNow());
  const effectiveFont = printing || !fontReady ? "GRUND" : font;

  useEffect(() => {
    let cancelled = false;
    const path = item.runId ? `/learning/runs/${encodeURIComponent(item.runId)}/display-settings` : "/learning/display-settings";
    api<LearningDisplay>(path).then((value) => { if (!cancelled) setDisplay(learningDisplayFrom(value)); })
      .catch(() => { if (!cancelled) setMessage("Die Schrifteinstellung konnte nicht geladen werden. Bitte lade die Übung erneut."); })
      .finally(() => { if (!cancelled) setSettingsLoaded(true); });
    return () => { cancelled = true; };
  }, [item.runId]);

  useEffect(() => {
    const started = (event: Event) => {
      const detail = (event as CustomEvent<{ runId?: string; learningDisplay?: unknown }>).detail;
      if (detail?.learningDisplay) setDisplay(learningDisplayFrom(detail.learningDisplay));
    };
    window.addEventListener(exerciseRunStartedEvent, started);
    return () => window.removeEventListener(exerciseRunStartedEvent, started);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!settingsLoaded) return;
    if (!document.fonts) { setLoadedFont(font); return; }
    const failed = () => { if (!cancelled) { setFailedFont(font); setMessage("Die gewählte Schrift konnte nicht geladen werden. Der Text erscheint in Druckschrift."); } };
    const fonts = font === "GRUND" ? ["Grund"] : ["Grund", font];
    Promise.all(fonts.map((name) => document.fonts.load(`400 24px "Playwrite DE ${name}"`))).then((loaded) => {
      if (cancelled) return;
      if (loaded.every((faces) => faces.length)) setLoadedFont(font);
      else failed();
    }).catch(failed);
    return () => { cancelled = true; };
  }, [font, settingsLoaded]);

  useEffect(() => { if (settingsLoaded && (fontReady || failedFont === font)) setPresented(true); }, [settingsLoaded, fontReady, failedFont, font]);

  useEffect(() => { setLocalHelp(null); }, [item.runId, item.itemKey]);
  useEffect(() => {
    if (!printing) return;
    const timer = window.setTimeout(() => setClock((value) => value + 1), Math.max(0, deadline - realtime.serverNow()));
    const visible = () => setClock((value) => value + 1);
    document.addEventListener("visibilitychange", visible);
    return () => { window.clearTimeout(timer); document.removeEventListener("visibilitychange", visible); };
  }, [printing, deadline, clock]);

  async function help() {
    setBusy(true); setMessage("");
    try {
      if (realtime.session) await realtime.requestPrintHelp(item.runId, item.itemKey);
      else setLocalHelp({ ...item, until: realtime.serverNow() + printHelpDurationMs });
    } catch (error) { setMessage(error instanceof Error ? error.message : "Die Druckschrift-Hilfe konnte nicht gestartet werden."); }
    finally { setBusy(false); }
  }

  return <ItemContext.Provider value={setItem}>
    <div className={`exercise-presentation${fontReady || failedFont === font ? "" : " learning-font-loading"}`} data-learning-font={effectiveFont} style={{ "--learning-font": `"Playwrite DE ${effectiveFont === "GRUND" ? "Grund" : effectiveFont}"` } as CSSProperties}>
      {handwriting && font !== "GRUND" && fontReady && display.allowPrintHelp && item.itemKey && <div className="print-help-toolbar">
        <button type="button" className="secondary small" disabled={busy || Boolean(realtime.session && (!item.runId || !realtime.synchronized))} onClick={() => void help()} aria-pressed={printing}>{printing ? "Druckschrift erneut für 30 Sekunden" : "Druckschrift für 30 Sekunden"}</button>
        {printing && <span role="status">Druckschrift ist vorübergehend aktiv.</span>}
      </div>}
      {message && <p className="alert info" role="status">{message}</p>}
      {presented ? <ExercisePauseBoundary exerciseType={exerciseType} itemRunId={item.runId} itemActive={Boolean(item.itemKey)} preparePause={item.preparePause}><ExerciseReflectionBoundary runId={item.runId} exerciseType={exerciseType}>{children}</ExerciseReflectionBoundary></ExercisePauseBoundary> : <p role="status">Die Übung wird vorbereitet …</p>}
    </div>
  </ItemContext.Provider>;
}
